import {
  connect as connectToTwilioRoom,
  ConnectOptions,
  LocalAudioTrack,
  LocalDataTrack,
  LocalTrack,
  LocalVideoTrack,
  RemoteParticipant,
  Room
} from 'twilio-video';
import { RpcCallPayload, RpcCommandPayload, SignalData } from '../../helpers/protocol/automatedParticipantProtocol';
import type { AutomatedParticipantBaseConfig, ParticipantLogEntry } from './types';
import { PenpalParticipantBridge } from './PenpalBridge';
import { CallManager, SignalingEvents } from '../../helpers/CallManager';

export type LocalMediaTrack = LocalAudioTrack | LocalVideoTrack;

/**
 * Abstract base class for automated participants in Twilio Video rooms.
 * Handles room connection, JSON-RPC protocol over data channels, and track management.
 * Derived classes implement the abstract protocol handlers for specific behavior.
 */
export abstract class AutomatedParticipantBase {
  protected readonly bridge: PenpalParticipantBridge;

  protected roomName?: string;

  protected readonly mediaOptions?: Partial<ConnectOptions>;

  protected room?: Room;

  protected dataTrack?: LocalDataTrack;

  protected mediaTracks: LocalTrack[] = [];

  protected callManager?: CallManager;

  private initializePromise?: Promise<void>;
  private participantConnectedListener?: (participant: RemoteParticipant) => void;
  private participantDisconnectedListener?: (participant: RemoteParticipant) => void;
  private roomDisconnectedListener?: (room: Room, error?: Error) => void;

  constructor(config: AutomatedParticipantBaseConfig) {
    this.bridge = config.bridge;
    this.roomName = config.roomName;
    this.mediaOptions = config.mediaOptions;
  }

  /**
   * Initializes the participant by connecting to the Twilio room.
   * Safe to call multiple times; subsequent calls return the same promise.
   */
  async initialize(): Promise<void> {
    if (this.room) return;
    this.initializePromise ??= this.establishRoomConnection();
    await this.initializePromise;
  }

  /**
   * Reconnects to the room by disconnecting and reinitializing.
   */
  async reconnect(): Promise<void> {
    await this.disconnect();
    await this.initialize();
  }

  /**
   * Checks if the participant is currently connected to the room.
   */
  isConnected(): boolean {
    return this.room?.state === 'connected';
  }

  /**
   * Gets the current Twilio room instance.
   */
  getRoom(): Room | undefined {
    return this.room;
  }

  /**
   * Gets the local data track used for JSON-RPC communication.
   */
  getDataTrack(): LocalDataTrack | undefined {
    return this.dataTrack;
  }

  /**
   * Establishes connection to the Twilio room using token from bridge.
   */
  protected async establishRoomConnection(): Promise<void> {
    try {
      const token = await this.bridge.getTwilioTestToken();
      this.roomName = token.roomName;
      this.mediaTracks = await this.createMediaTracks();
      this.dataTrack = this.createDataTrack();
      const connectOptions: ConnectOptions = {
        ...this.mediaOptions,
        name: this.roomName,
        tracks: [...this.mediaTracks, this.dataTrack]
      };
      this.room = await connectToTwilioRoom(token.token, connectOptions);

      const handlers: Partial<SignalingEvents> = {
        onOfferToStart: (payload) => this.onOfferToStart(payload),
        onOfferToStartCancelled: (payload) => this.onOfferToStartCancelled(payload),
        onStartRecording: (payload) => this.onStartRecording(payload),
        onStopRecording: (payload) => this.onStopRecording(payload),
        onReplayRecording: (payload) => this.onReplayRecording(payload),
        onEndRecordingSession: (payload) => this.onEndRecordingSession(payload),
        onCallAccepted: (senderId, callId) => this.onCallAccepted(senderId, callId),
        onCallDeclined: (senderId, callId) => this.onCallDeclined(senderId, callId),
        onRequestPromptVideo: (senderId, data) => this.onRequestPromptVideo(senderId, data)
      };
      this.callManager = new CallManager(this.room, this.dataTrack, handlers);

      this.attachRoomListeners(this.room);

      await this.onRoomConnected(this.room);
    } catch (error) {
      await this.logEvent({
        level: 'error',
        message: 'Automated participant failed to connect',
        context: {
          error: error instanceof Error ? error.message : String(error),
          roomName: this.roomName
        }
      });
      throw error;
    } finally {
      this.initializePromise = undefined;
    }
  }

  protected createDataTrack(): LocalDataTrack {
    return new LocalDataTrack();
  }

  // Default implementation publishes no media. Derived classes can override.
  protected async createMediaTracks(): Promise<LocalTrack[]> {
    return [];
  }

  protected async onRoomConnected(_room: Room): Promise<void> {
    // subclasses may override
  }

  protected async onRoomDisconnected(_room: Room, _error?: Error): Promise<void> {
    // subclasses may override
  }

  protected onParticipantConnected(_participant: RemoteParticipant): void {
    // subclasses may override
  }

  protected onParticipantDisconnected(_participant: RemoteParticipant): void {
    // subclasses may override
  }

  /** Called when clinician sends an offer to start the call. */
  protected abstract onOfferToStart(payload: RpcCallPayload): Promise<void> | void;

  /** Called when clinician cancels the call offer. */
  protected abstract onOfferToStartCancelled(payload: RpcCallPayload): Promise<void> | void;

  /** Called when clinician requests to start recording. */
  protected abstract onStartRecording(payload: RpcCommandPayload): Promise<void> | void;

  /** Called when clinician requests to stop recording. */
  protected abstract onStopRecording(payload: RpcCommandPayload): Promise<void> | void;

  /** Called when clinician requests to replay the recording. */
  protected abstract onReplayRecording(payload: RpcCommandPayload): Promise<void> | void;

  /** Called when clinician ends the recording session. */
  protected abstract onEndRecordingSession(payload: RpcCommandPayload): Promise<void> | void;

  protected onCallAccepted(senderId: string, callId: string): void {}

  protected onCallDeclined(_senderId: string, _callId: string): boolean {
    return true;
  }

  protected onRequestPromptVideo(senderId: string, data?: Record<string, unknown>): void {}

  /** Sends offer accepted response to clinician. */
  protected sendOfferAccepted(payload: RpcCallPayload) {
    if (!this.callManager) {
      throw new Error('CallManager is not initialized');
    }
    return this.callManager.acceptOffer(payload.data);
  }

  /** Sends prompt video complete notification to clinician. */
  protected sendPromptVideoComplete(data?: SignalData) {
    if (!this.callManager) {
      throw new Error('CallManager is not initialized');
    }
    return this.callManager.sendPromptVideoComplete(data);
  }

  /** Sends recording started notification to clinician. */
  protected sendRecordingStarted(data?: SignalData) {
    if (!this.callManager) {
      throw new Error('CallManager is not initialized');
    }
    return this.callManager.sendRecordingStarted(data);
  }

  /** Sends recording stopped notification to clinician. */
  protected sendRecordingStopped(data?: SignalData) {
    if (!this.callManager) {
      throw new Error('CallManager is not initialized');
    }
    return this.callManager.sendRecordingStopped(data);
  }

  /** Sends replay complete notification to clinician. */
  protected sendReplayComplete(data?: SignalData) {
    if (!this.callManager) {
      throw new Error('CallManager is not initialized');
    }
    return this.callManager.sendReplayComplete(data);
  }

  /** Sends thank you video complete notification to clinician. */
  protected sendThankYouVideoComplete(data?: SignalData) {
    if (!this.callManager) {
      throw new Error('CallManager is not initialized');
    }
    return this.callManager.sendThankYouVideoComplete(data);
  }

  /**
   * Disconnects from the room and cleans up all tracks and listeners.
   */
  async disconnect(): Promise<void> {
    this.mediaTracks.forEach((track) => {
      if ('stop' in track && typeof track.stop === 'function') {
        track.stop();
      }
    });
    this.mediaTracks = [];
    if (this.dataTrack && 'stop' in this.dataTrack && typeof this.dataTrack.stop === 'function') {
      this.dataTrack.stop();
    }
    this.dataTrack = undefined;
    if (this.callManager) {
      this.callManager.disconnect();
      this.callManager = undefined;
    }
    this.teardownRoomListeners();
    if (this.room) {
      this.room.disconnect();
      await this.onRoomDisconnected(this.room);
    }
    this.room = undefined;
  }

  /**
   * Logs an event via the Penpal bridge to the parent window.
   */
  protected async logEvent(entry: ParticipantLogEntry) {
    return this.bridge.logEvent(entry);
  }

  private attachRoomListeners(room: Room) {
    this.participantConnectedListener = (participant: RemoteParticipant) => {
      this.onParticipantConnected(participant);
    };
    this.participantDisconnectedListener = (participant: RemoteParticipant) => {
      this.onParticipantDisconnected(participant);
    };
    this.roomDisconnectedListener = (_room: Room, error?: Error) => {
      this.onRoomDisconnected(_room, error);
      this.teardownRoomListeners();
    };

    room.on('participantConnected', this.participantConnectedListener);
    room.on('participantDisconnected', this.participantDisconnectedListener);
    room.once('disconnected', this.roomDisconnectedListener);

    room.participants.forEach((participant) => {
      this.onParticipantConnected(participant);
    });
  }

  private teardownRoomListeners() {
    if (!this.room) return;
    if (this.participantConnectedListener) {
      this.room.removeListener('participantConnected', this.participantConnectedListener);
      this.participantConnectedListener = undefined;
    }
    if (this.participantDisconnectedListener) {
      this.room.removeListener('participantDisconnected', this.participantDisconnectedListener);
      this.participantDisconnectedListener = undefined;
    }
    if (this.roomDisconnectedListener) {
      this.room.removeListener('disconnected', this.roomDisconnectedListener);
      this.roomDisconnectedListener = undefined;
    }
  }
}
